import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

/**
 * Hai cột kéo giãn được — chỉ từ màn lg trở lên. Nhỏ hơn: `small="hide"` (mặc định)
 * thì `right` ẩn (form có nút "View PDF" riêng); `small="stack"` thì `right` xếp
 * xuống dưới `left` (trang chi tiết: tiến trình duyệt vẫn phải thấy trên điện thoại).
 *
 * Không dùng components/common/SplitterPanel vì: (1) nó đặt transform lên panel →
 * mọi popover position:fixed trong form bị neo theo panel; (2) khung PDF là iframe
 * nuốt sự kiện chuột khi kéo ngang qua — ở đây phủ một lớp trong suốt lúc kéo.
 *
 * Tay nắm: kéo · double-click = về mặc định · ←/→ = 2% · nút ‹/› ẩn/hiện cột phải.
 * Tỉ lệ + trạng thái ẩn nhớ theo trình duyệt (localStorage, hỏng thì dùng mặc định).
 *
 * Ẩn/hiện có thể điều khiển từ ngoài (`collapsed` + `onCollapsedChange`) khi trang có
 * nút riêng cho việc đó (vd. nút sidebar trên header trang chi tiết) — hai nút chung
 * một trạng thái. `rightId` gắn lên cột phải cho aria-controls của nút ngoài.
 */
const KEY_STEP = 2;

const read = (key, fallback) => {
  try {
    const v = JSON.parse(window.localStorage.getItem(key));
    return v && typeof v === "object" ? { ...fallback, ...v } : fallback;
  } catch {
    return fallback;
  }
};
const write = (key, value) => {
  try { window.localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage tắt */ }
};

export const clampPct = (pct, min, max) => Math.min(max, Math.max(min, Math.round(Number(pct) * 10) / 10));

const SplitPane = ({
  left, right, storageKey, defaultLeft = 60, min = 35, max = 75, rightLabel = "preview",
  collapsed: controlledCollapsed, onCollapsedChange, rightId, className = "", small = "hide",
}) => {
  const stack = small === "stack";
  const containerRef = useRef(null);
  const [inner, setState] = useState(() => read(storageKey, { left: defaultLeft, collapsed: false }));
  const controlled = typeof controlledCollapsed === "boolean";
  const state = controlled ? { ...inner, collapsed: controlledCollapsed } : inner;
  const setCollapsed = (next) => (controlled ? onCollapsedChange?.(next) : setState((s) => ({ ...s, collapsed: next })));
  const [dragging, setDragging] = useState(false);
  const latest = useRef(state);

  useEffect(() => { latest.current = inner; if (!dragging) write(storageKey, inner); }, [inner, dragging, storageKey]);

  const setLeft = useCallback((pct) => setState((s) => ({ ...s, left: clampPct(pct, min, max) })), [min, max]);

  const startDrag = (e) => {
    if (state.collapsed || e.button > 0) return;
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    const onMove = (ev) => setLeft(((ev.clientX - rect.left) / rect.width) * 100);
    const stop = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", stop);
      setDragging(false);
      write(storageKey, latest.current);
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", stop);
    setDragging(true);
  };

  const toggle = (e) => {
    e.stopPropagation();
    setCollapsed(!state.collapsed);
  };

  const leftWidth = state.collapsed ? "100%" : `${state.left}%`;

  return (
    <div ref={containerRef} className={`tw-flex tw-flex-1 tw-min-h-0 ${stack ? "tw-flex-col tw-gap-4 lg:tw-flex-row lg:tw-gap-0" : ""} ${className}`}>
      <div className="tw-min-w-0 tw-min-h-0 tw-flex tw-flex-col tw-w-full lg:tw-w-[var(--split-left)] lg:tw-flex-none"
        style={{ "--split-left": leftWidth }}>
        {left}
      </div>

      {/* Tay nắm — chỉ lg+. Vạch mảnh, đậm lên khi rê/kéo; nút ẩn/hiện nằm giữa. */}
      <div role="separator" aria-orientation="vertical" aria-label={`Resize ${rightLabel}`}
        aria-valuemin={min} aria-valuemax={max} aria-valuenow={state.collapsed ? 100 : state.left}
        tabIndex={0} title="Drag to resize · double-click to reset"
        onPointerDown={startDrag}
        onDoubleClick={() => { setState((s) => ({ ...s, left: defaultLeft })); setCollapsed(false); }}
        onKeyDown={(e) => {
          if (state.collapsed) return;
          if (e.key === "ArrowLeft") { e.preventDefault(); setLeft(state.left - KEY_STEP); }
          if (e.key === "ArrowRight") { e.preventDefault(); setLeft(state.left + KEY_STEP); }
        }}
        className={`tw-group tw-relative tw-hidden lg:tw-flex tw-flex-none tw-w-5 tw-flex-col tw-items-center tw-touch-none focus:tw-outline-none ${
          state.collapsed ? "tw-cursor-default" : "tw-cursor-col-resize"
        }`}>
        {/* Vạch chia chạy hết chiều cao cột. */}
        <span aria-hidden="true" className={`tw-absolute tw-inset-y-0 tw-left-1/2 -tw-translate-x-1/2 tw-w-px tw-transition-colors ${
          dragging ? "tw-bg-geist-accent" : "tw-bg-geist-border group-hover:tw-bg-geist-accent/50 group-focus-visible:tw-bg-geist-accent"
        }`} />
        {/* Nút ẩn/hiện DÍNH trong khung nhìn (sticky): trang chi tiết cuộn cả trang nên
            cột rất cao — đặt top-1/2 của cột là nút trôi ra ngoài màn hình. Màu nhấn
            (accent) để nhận ra ngay; viền + chevron đổi chiều theo trạng thái. */}
        <div className="tw-sticky tw-top-[38vh] tw-mt-[38vh] tw-z-10">
          <button type="button" onClick={toggle} onPointerDown={(e) => e.stopPropagation()}
            aria-label={state.collapsed ? `Show ${rightLabel}` : `Hide ${rightLabel}`}
            title={state.collapsed ? `Show ${rightLabel}` : `Hide ${rightLabel}`}
            className="tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-11 tw-p-0 tw-rounded-full tw-border tw-border-solid tw-border-geist-accent/40 tw-bg-[#eef4ff] tw-text-geist-accent tw-shadow-geist-card tw-cursor-pointer tw-transition-colors hover:tw-bg-geist-accent hover:tw-text-white hover:tw-border-geist-accent focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-geist-accent/40">
            <i className={`bi ${state.collapsed ? "bi-chevron-double-left" : "bi-chevron-double-right"} tw-text-[11px] tw-font-bold`} aria-hidden="true" />
          </button>
        </div>
      </div>

      <div id={rightId} className={`tw-min-w-0 tw-min-h-0 tw-flex-1 tw-flex-col ${
        state.collapsed ? "tw-hidden" : stack ? "tw-flex" : "tw-hidden lg:tw-flex"}`}>
        {right}
      </div>

      {/* Lúc kéo: phủ trong suốt toàn màn — iframe PDF không nuốt mất pointermove. */}
      {dragging && <div aria-hidden="true" className="tw-fixed tw-inset-0 tw-z-[100050] tw-cursor-col-resize" />}
    </div>
  );
};

SplitPane.propTypes = {
  left: PropTypes.node.isRequired,
  right: PropTypes.node.isRequired,
  storageKey: PropTypes.string.isRequired,
  defaultLeft: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  rightLabel: PropTypes.string,
  collapsed: PropTypes.bool,
  onCollapsedChange: PropTypes.func,
  rightId: PropTypes.string,
  className: PropTypes.string,
  small: PropTypes.oneOf(["hide", "stack"]),
};

export default SplitPane;
